import { Link } from 'react-router-dom'
import { Mail, MessageCircle, FileText, HelpCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  StitchRefinedPageLayout,
  stitchFeatureIconCircle,
  stitchLead,
  stitchPageTitleLg,
  stitchTonalCard,
} from '@/components/layout/StitchRefinedPageLayout'

const Support = () => {
  return (
    <StitchRefinedPageLayout contentMax="4xl">
      <div className="mb-12 text-center md:mb-16">
        <h1 className={`${stitchPageTitleLg} mb-4 md:mb-6`}>Support Center</h1>
        <p className={`${stitchLead} mx-auto max-w-2xl text-lg md:text-xl`}>
          We&apos;re here to help you get the most out of Vesta.
        </p>
        <p className="mx-auto mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm text-vesta-navy/70">
          <Link
            to="/status"
            className="font-medium text-vesta-navy underline-offset-2 hover:text-vesta-gold hover:underline"
          >
            System status
          </Link>
          <span aria-hidden className="text-vesta-navy/30">
            ·
          </span>
          <Link
            to="/trust"
            className="font-medium text-vesta-navy underline-offset-2 hover:text-vesta-gold hover:underline"
          >
            Trust &amp; vendors (DPA)
          </Link>
        </p>
      </div>

      <div className="mb-16 grid gap-6 md:grid-cols-3">
        <div className={`${stitchTonalCard} p-6 transition-shadow hover:shadow-[0_12px_48px_-16px_rgba(27,58,92,0.14)] md:p-8`}>
          <div className="mb-4 flex items-center">
            <div className={cn(stitchFeatureIconCircle, 'mr-4 h-12 w-12 rounded-xl')}>
              <Mail className="h-6 w-6" aria-hidden />
            </div>
            <h2 className="font-stitch text-xl font-semibold text-vesta-navy">Email Support</h2>
          </div>
          <p className="mb-6 text-vesta-navy/80">
            Get help with technical issues or general questions about Vesta. We typically respond within 24 hours.
          </p>
          <a
            href="mailto:vestacfo@gmail.com"
            className="inline-flex items-center font-medium text-vesta-navy-muted transition-colors hover:text-vesta-gold"
          >
            vestacfo@gmail.com
          </a>
        </div>

        <div className={`${stitchTonalCard} p-6 transition-shadow hover:shadow-[0_12px_48px_-16px_rgba(27,58,92,0.14)] md:p-8`}>
          <div className="mb-4 flex items-center">
            <div className={cn(stitchFeatureIconCircle, 'mr-4 h-12 w-12 rounded-xl')}>
              <MessageCircle className="h-6 w-6" aria-hidden />
            </div>
            <h2 className="font-stitch text-xl font-semibold text-vesta-navy">Live Chat</h2>
          </div>
          <p className="mb-6 text-vesta-navy/80">
            Chat with our AI assistant for instant answers, or get connected to our support team.
          </p>
          <Link
            to="/chat"
            className="inline-flex items-center font-medium text-vesta-navy-muted transition-colors hover:text-vesta-gold"
          >
            Start Chat →
          </Link>
        </div>

        <div className={`${stitchTonalCard} p-6 transition-shadow hover:shadow-[0_12px_48px_-16px_rgba(27,58,92,0.14)] md:p-8`}>
          <div className="mb-4 flex items-center">
            <div className={cn(stitchFeatureIconCircle, 'mr-4 h-12 w-12 rounded-xl')}>
              <FileText className="h-6 w-6" aria-hidden />
            </div>
            <h2 className="font-stitch text-xl font-semibold text-vesta-navy">Getting started</h2>
          </div>
          <p className="mb-6 text-vesta-navy/80">
            Step-by-step guide to connect your PMS, import data, and open your first dashboard.
          </p>
          <Link
            to="/how-to-get-started"
            className="inline-flex items-center font-medium text-vesta-navy-muted transition-colors hover:text-vesta-gold"
          >
            View guide →
          </Link>
        </div>
      </div>

      <div className="mb-16">
        <h2 className="mb-8 text-center font-stitch text-2xl font-semibold text-vesta-navy md:text-3xl">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          <div className={`${stitchTonalCard} p-6`}>
            <h3 className="mb-2 flex items-center font-stitch font-semibold text-vesta-navy">
              <HelpCircle className="mr-2 h-5 w-5 text-vesta-gold" aria-hidden />
              Which PMS systems does Vesta support?
            </h3>
            <p className="text-vesta-navy/80">
              <strong className="text-vesta-navy">Mews</strong> is available today with automatic daily sync for occupancy, ADR, RevPAR, and room revenue.
              <strong className="text-vesta-navy"> Cloudbeds</strong> and <strong className="text-vesta-navy">Oracle Opera</strong> are on the roadmap — email{' '}
              <a href="mailto:vestacfo@gmail.com" className="font-medium text-vesta-navy hover:text-vesta-gold hover:underline">
                vestacfo@gmail.com
              </a>{' '}
              for early access. Any PMS can use CSV import or manual KPI entry in the app in the meantime. See the full list on{' '}
              <Link to="/integrations" className="font-medium hover:underline">integrations</Link>.
            </p>
          </div>

          <div className={`${stitchTonalCard} p-6`}>
            <h3 className="mb-2 flex items-center font-stitch font-semibold text-vesta-navy">
              <HelpCircle className="mr-2 h-5 w-5 text-vesta-gold" aria-hidden />
              How are RevPAR, ADR, and GOPPAR calculated?
            </h3>
            <p className="text-vesta-navy/80">
              Vesta derives hotel KPIs from your synced daily metrics — the same rows that power the dashboard and morning briefing.
              RevPAR is room revenue divided by available room nights; ADR is revenue per occupied room; GOPPAR reflects gross operating
              profit per available room. Your PMS night audit remains the system of record — use Vesta for trends, alerts, and owner-ready context.
            </p>
          </div>

          <div className={`${stitchTonalCard} p-6`}>
            <h3 className="mb-2 flex items-center font-stitch font-semibold text-vesta-navy">
              <HelpCircle className="mr-2 h-5 w-5 text-vesta-gold" aria-hidden />
              How secure is my property data?
            </h3>
            <p className="text-vesta-navy/80">
              Data is encrypted at rest (AES-256) and in transit (TLS 1.3). We follow SOC 2-aligned practices, support GDPR/CCPA requests,
              and never sell your data. See our <Link to="/security" className="font-medium hover:underline">security page</Link> and{' '}
              <Link to="/trust" className="font-medium hover:underline">subprocessors list</Link>.
            </p>
          </div>

          <div className={`${stitchTonalCard} p-6`}>
            <h3 className="mb-2 flex items-center font-stitch font-semibold text-vesta-navy">
              <HelpCircle className="mr-2 h-5 w-5 text-vesta-gold" aria-hidden />
              Is Vesta a financial advisor?
            </h3>
            <p className="text-vesta-navy/80">
              No. Vesta CFO is not a registered financial advisor, broker, or investment firm. AI briefings and alerts are for operational
              insight only — consult qualified professionals before major financing or investment decisions.
            </p>
          </div>

          <div className={`${stitchTonalCard} p-6`}>
            <h3 className="mb-2 flex items-center font-stitch font-semibold text-vesta-navy">
              <HelpCircle className="mr-2 h-5 w-5 text-vesta-gold" aria-hidden />
              Can I cancel my subscription anytime?
            </h3>
            <p className="text-vesta-navy/80">
              Yes, you can cancel your subscription at any time. Your access will continue until the end of your current
              billing period.
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-gradient-to-br from-white via-vesta-cream to-vesta-mist/50 p-8 text-center ring-1 ring-vesta-navy/[0.08] shadow-[0_12px_48px_-20px_rgba(27,58,92,0.12)] md:p-12">
        <Mail className="mx-auto mb-4 h-12 w-12 text-vesta-gold" aria-hidden />
        <h3 className="mb-2 font-stitch text-xl font-semibold text-vesta-navy">Still have questions?</h3>
        <p className="mb-6 text-vesta-navy/80">Our team is happy to help with anything not covered above.</p>
        <a
          href="mailto:vestacfo@gmail.com"
          className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-vesta-navy to-vesta-navy-muted px-6 py-3 font-stitch text-sm font-semibold text-white shadow-md transition-opacity hover:opacity-95"
        >
          Contact Support
        </a>
      </div>
    </StitchRefinedPageLayout>
  )
}

export default Support
